import {isNewAuthSystemEnabled} from '@apis/auth'
import {getAuthConfig} from '@shared/utils/authConfig'

/**
 * Best-effort OAuth token revocation on logout (RFC 7009, AB#6319 / pentest F-20).
 *
 * Clearing localStorage alone leaves the bearer token valid on the auth
 * provider until it expires, so a copied token keeps working after logout.
 * `revoke_token/` revokes this device's token pair and ends the auth-site
 * session; other devices and connectors are untouched.
 *
 * Logout must never hang on this: both requests run concurrently, are aborted
 * after REVOKE_TIMEOUT_MS, and every failure is swallowed. Token values are
 * never logged.
 */
export const REVOKE_TIMEOUT_MS = 3000

/**
 * Marker the Alpinist embed writes for the anonymous demo token it minted
 * (`DEMO_SESSION_META_KEY` in src/embed/demoSession.ts). Inlined rather than
 * imported so the logout path doesn't pull the embed module into the app.
 */
const DEMO_SESSION_META_KEY = 'alpinist_demo_session'

const isDemoToken = (token: string): boolean => {
  try {
    const raw = localStorage.getItem(DEMO_SESSION_META_KEY)
    return !!raw && (JSON.parse(raw) as {token?: unknown})?.token === token
  } catch {
    return false
  }
}

type TokenTypeHint = 'access_token' | 'refresh_token'

const postRevoke = async (
  url: string,
  clientId: string,
  accessToken: string,
  token: string,
  tokenTypeHint: TokenTypeHint,
  signal: AbortSignal,
): Promise<void> => {
  try {
    await fetch(url, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        token,
        token_type_hint: tokenTypeHint,
        client_id: clientId,
      }).toString(),
      signal,
    })
  } catch {
    // Best effort: network errors, aborts and CORS failures must not block logout.
  }
}

const revoke = async (accessToken: string, refreshToken: string | null): Promise<void> => {
  let url: string
  let clientId: string
  try {
    const config = getAuthConfig()
    url = `${config.authProviderBaseUrl}/auth/api/revoke_token/`
    clientId = config.clientId
  } catch {
    // Missing auth env: nothing to revoke against.
    return
  }

  const controller = new AbortController()
  const requests = [
    postRevoke(url, clientId, accessToken, accessToken, 'access_token', controller.signal),
  ]
  if (refreshToken) {
    requests.push(
      postRevoke(url, clientId, accessToken, refreshToken, 'refresh_token', controller.signal),
    )
  }

  await new Promise<void>((resolve) => {
    // Resolve on the timer as well as on abort, so a fetch that ignores the
    // abort signal still can't hold logout past the deadline.
    const timer = setTimeout(() => {
      controller.abort()
      resolve()
    }, REVOKE_TIMEOUT_MS)
    void Promise.allSettled(requests).then(() => {
      clearTimeout(timer)
      resolve()
    })
  })
}

let inFlight: Promise<void> | null = null

/**
 * Revokes the provider tokens currently in localStorage. Resolves (never
 * rejects) once both revocations settled or REVOKE_TIMEOUT_MS elapsed.
 *
 * The tokens are read synchronously at call time, so a caller may clear
 * storage right after calling without awaiting. Concurrent callers (several
 * requests 401ing at once, a double-clicked logout) share one revocation.
 * A no-op without provider tokens: legacy auth system, no token stored, or
 * the embed's anonymous demo token.
 */
export const revokeProviderTokens = (): Promise<void> => {
  if (inFlight) {
    return inFlight
  }

  const accessToken = localStorage.getItem('token')
  if (!isNewAuthSystemEnabled || !accessToken || isDemoToken(accessToken)) {
    return Promise.resolve()
  }

  inFlight = revoke(accessToken, localStorage.getItem('refresh_token')).finally(() => {
    inFlight = null
  })
  return inFlight
}
